/**
 * Saved-query DSL — the persisted shape of a named, reusable query (ERP E1,
 * roadmap #455, PR-5/7).
 *
 * A saved query stores ONLY the transport-safe `where` filter tree: the DATA that
 * describes "which rows". It deliberately does NOT persist:
 *   - `pinned` leaves — those are a LIST-TOOL runtime concern (status/priority/q →
 *     hand-normalised typed params, built per-call from already-validated tool
 *     inputs). A saved query is re-runnable structured DATA, not a frozen tool
 *     invocation, so `pinned` is intentionally omitted from the stored shape (a
 *     bare `z.object` STRIPS any unknown key, so a caller that sends `pinned` has it
 *     dropped rather than persisted). PR body notes this explicitly.
 *   - `tenantId` / `adminOverride` — NEVER stored on the row. The runner supplies
 *     the tenant posture from the EXECUTING context (`query.saved.run`), so a stored
 *     query can never smuggle another tenant's scope. The compiler (server-side)
 *     owns the tenant base condition; the stored DSL is pure filter DATA.
 *
 * `where` reuses the SAME `queryWhereSchema` the list/aggregate tools accept, so a
 * saved query is exactly as depth/node/injection-guarded as an inline `where` — the
 * shared schema's superRefine (depth + total-node caps) runs on parse.
 */
import { z } from 'zod';
import { queryWhereSchema } from './dsl.js';
/**
 * The persisted DSL for a saved query. `where` is optional — a saved query with no
 * `where` means "all rows" (tenant-scoped by the runner). Unknown keys are STRIPPED
 * (bare `z.object`), so `pinned` (or any other runtime-only field) is never stored.
 */
export const savedQueryDslSchema = z.object({
    where: queryWhereSchema.optional(),
});
/** Visibility tier of a saved query. */
export const SAVED_QUERY_VISIBILITY = ['private', 'tenant'];
//# sourceMappingURL=savedQuery.js.map